// Recidiviz - a data platform for criminal justice reform
// Copyright (C) 2025 Recidiviz, Inc.
//
// This program is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as published by
// the Free Software Foundation, either version 3 of the License, or
// (at your option) any later version.
//
// This program is distributed in the hope that it will be useful,
// but WITHOUT ANY WARRANTY; without even the implied warranty of
// MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
// GNU General Public License for more details.
//
// You should have received a copy of the GNU General Public License
// along with this program.  If not, see <https://www.gnu.org/licenses/>.
// =============================================================================

import { OpportunityTableColumnId } from "../../../../../../core/OpportunityCaseloadView/HydratedOpportunityPersonList";
import { PartialRecord } from "../../../../../../utils/typeUtils";
import { OpportunityTab, OpportunityTabGroups } from "../../../../types";
import { ApiOpportunityConfiguration } from "../../ApiOpportunityConfigurationImpl";

export class UsAzTransferToAdministrativeSupervisionConfiguration extends ApiOpportunityConfiguration {
  countFromTabCounts = (counts: PartialRecord<OpportunityTab, number>) =>
    (counts["Eligible per ORAS"] ?? 0) +
    (counts["Eligible per Initial Assessment"] ?? 0);

  get customSubmittedText(): string {
    return "Don't forget to remove them from the [drug testing schedule](https://aversys.averhealth.com).";
  }

  get enabledColumns(): Array<OpportunityTableColumnId> {
    const cols = [...super.enabledColumns];
    const colsToAdd: OpportunityTableColumnId[] = [
      "ALMOST_ELIGIBILITY_DATE",
    ].filter(
      (c) => !cols.includes(c as OpportunityTableColumnId),
    ) as OpportunityTableColumnId[];
    const colsToRemove: OpportunityTableColumnId[] = ["STATUS"];
    return [...cols, ...colsToAdd].filter((c) => !colsToRemove.includes(c));
  }

  get tabGroups(): OpportunityTabGroups {
    if (!this.supportsSupervisorReviewOnGrants) return super.tabGroups;
    return {
      "ELIGIBILITY STATUS": [
        "Eligible per ORAS",
        "Eligible per Initial Assessment",
        this.supervisorReviewTabTitle,
        this.grantApprovedTabTitle,
        this.submittedTabTitle,
        this.deniedTabTitle,
      ],
    };
  }

  get supportsSupervisorReviewOnGrants(): boolean {
    return !!this.userStore.activeFeatureVariants
      .usAzAdminSupervisionApprovalFlow;
  }

  get supervisorReviewTabTitle(): OpportunityTab {
    return "Submitted for Supervisor Review";
  }

  get grantApprovedTabTitle(): OpportunityTab {
    return "Approved by Supervisor";
  }

  get grantApprovedStatusMessage(): string {
    return "Approved by Supervisor";
  }

  get grantReviewDropdownLabel(): string {
    return "Submit for Supervisor Approval";
  }
}
